import { z as zod } from 'zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Box,
    Stepper,
    Step,
    StepLabel,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { toast } from 'sonner';

import { Form, Field } from 'src/components/hook-form';
import { API_CONFIG } from 'src/config/api';
import { STORAGE_KEY } from 'src/auth/context/jwt';

// ----------------------------------------------------------------------

const typesEst = [
    { id: '0', name: 'Salão de Beleza', value: 'SALAO_DE_BELEZA' },
    { id: '1', name: 'Clínica de Estética', value: 'CLINICA_DE_ESTETICA' },
    { id: '2', name: 'Clínica de Massagem', value: 'CLINICA_DE_MASSAGEM' },
    { id: '3', name: 'Studio de beleza', value: 'STUDIO_DE_BELEZA' },
    { id: '4', name: 'Spa', value: 'SPA' },
    { id: '5', name: 'Barbearia', value: 'BARBEARIA' },
    { id: '6', name: 'Esmalteria', value: 'ESMALTERIA' },
    { id: '7', name: 'Espaço infantil', value: 'ESPACO_INFANTIL' },
    { id: '8', name: 'Podologia', value: 'PODOLOGIA' },
    { id: '9', name: 'Centro de depilação', value: 'CENTRO_DE_DEPILACAO' },
    { id: '10', name: 'Espaço de beleza', value: 'ESPACO_DE_BELEZA' },
    { id: '11', name: 'Maquiadora', value: 'MAQUIADORA' },
    { id: '12', name: 'Outros', value: 'OUTROS' }
];

const typeHealth = [
    { id: '0', name: 'Fisioterapia', value: 'FISIOTERAPIA' },
    { id: '1', name: 'Odontologia', value: 'ODONTOLOGIA' },
    { id: '2', name: 'Nutrição', value: 'NUTRICAO' },
    { id: '3', name: 'Psicologia', value: 'PSICOLOGIA' },
    { id: '4', name: 'Psiquiatria', value: 'PSIQUIATRIA' },
    { id: '5', name: 'Psicopedagogia', value: 'PSICOPEDAGOGIA' },
    { id: '6', name: 'Clínica de Emagrecimento e Bem-Estar', value: 'CLINICA_EMAGRECIMENTO_BEM_ESTAR' },
    { id: '7', name: 'Yoga e Meditação', value: 'YOGA_E_MEDITACAO' },
    { id: '8', name: 'Pediatria', value: 'PEDIATRIA' },
    { id: '9', name: 'Medicina Alternativa', value: 'MEDICINA_ALTERNATIVA' },
    { id: '10', name: 'Clínica Geral', value: 'CLINICA_GERAL' },
    { id: '11', name: 'Clínica de Reabilitação', value: 'CLINICA_DE_REABILITACAO' },
    { id: '12', name: 'Saúde Ocupacional', value: 'SAUDE_OCUPACIONAL' },
    { id: '13', name: 'Vacinação', value: 'VACINACAO' },
    { id: '14', name: 'Ginecologia e Obstetrícia', value: 'GINECOLOGIA_E_OBSTETRICIA' },
    { id: '15', name: 'Medicina Preventiva', value: 'MEDICINA_PREVENTIVA' },
    { id: '16', name: 'Oftalmologia', value: 'OFTALMOLOGIA' },
    { id: '17', name: 'Dermatologia', value: 'DERMATOLOGIA' },
    { id: '18', name: 'Cirurgião Plástico', value: 'CIRURGIAO_PLASTICO' },
    { id: '19', name: 'Outros', value: 'OUTROS' }
];

const estadosEst = {
    AC: { id: 0, nome: "Acre" },
    AL: { id: 1, nome: "Alagoas" },
    AM: { id: 2, nome: "Amazonas" },
    AP: { id: 3, nome: "Amapá" },
    BA: { id: 4, nome: "Bahia" },
    CE: { id: 5, nome: "Ceará" },
    DF: { id: 6, nome: "Distrito Federal" },
    ES: { id: 7, nome: "Espírito Santo" },
    GO: { id: 8, nome: "Goiás" },
    MA: { id: 9, nome: "Maranhão" },
    MG: { id: 10, nome: "Minas Gerais" },
    MS: { id: 11, nome: "Mato Grosso do Sul" },
    MT: { id: 12, nome: "Mato Grosso" },
    PA: { id: 13, nome: "Pará" },
    PB: { id: 14, nome: "Paraíba" },
    PE: { id: 15, nome: "Pernambuco" },
    PI: { id: 16, nome: "Piauí" },
    PR: { id: 17, nome: "Paraná" },
    RJ: { id: 18, nome: "Rio de Janeiro" },
    RN: { id: 19, nome: "Rio Grande do Norte" },
    RO: { id: 20, nome: "Rondônia" },
    RR: { id: 21, nome: "Roraima" },
    RS: { id: 22, nome: "Rio Grande do Sul" },
    SC: { id: 23, nome: "Santa Catarina" },
    SE: { id: 24, nome: "Sergipe" },
    SP: { id: 25, nome: "São Paulo" },
    TO: { id: 26, nome: "Tocantins" }
};

const CompleteProfileSchema = zod.object({
    cpf_cnpj: zod.string().min(11, { message: 'CPF/CNPJ é obrigatório!' }),
    nomeDoEstabelecimento: zod.string().optional(),
    nome: zod.string().min(3, { message: 'Nome do responsável é obrigatório!' }),
    qta: zod.string().min(1, { message: 'Quantidade de profissionais é obrigatória!' }),
    ddd: zod.string().min(2, { message: 'DDD é obrigatório!' }),
    phone: zod.string().min(8, { message: 'Telefone é obrigatório!' }),
    cep: zod.string().min(8, { message: 'CEP é obrigatório!' }),
    address: zod.string().min(3, { message: 'Endereço é obrigatório!' }),
    numeroEndereco: zod.string().min(1, { message: 'Número é obrigatório!' }),
    neighborhood: zod.string().min(3, { message: 'Bairro é obrigatório!' }),
    city: zod.string().min(3, { message: 'Cidade é obrigatória!' }),
    state: zod.string().min(2, { message: 'Estado é obrigatório!' }),
    complement: zod.string().optional(),
});

// ----------------------------------------------------------------------

export function CompleteProfileModal({ open, onClose, establishment, onSuccess }) {
    const [activeStep, setActiveStep] = useState(0);
    const [typeDocument, setTypeDocument] = useState('CPF');
    const [typePhone, setTypePhone] = useState('CELULAR');
    const [ibge, setIbge] = useState(null);

    const steps = ['Documento', 'Informações', 'Contato', 'Endereço'];

    // Função para aplicar máscara de CPF
    const maskCPF = (value) =>
        value
            .replace(/\D/g, '') // Remove tudo que não é dígito
            .replace(/(\d{3})(\d)/, '$1.$2') // Coloca ponto após os 3 primeiros dígitos
            .replace(/(\d{3})(\d)/, '$1.$2') // Coloca ponto após os 3 segundos dígitos
            .replace(/(\d{3})(\d{1,2})/, '$1-$2') // Coloca hífen após os 3 terceiros dígitos
            .replace(/(-\d{2})\d+?$/, '$1'); // Limita a 11 dígitos

    // Função para aplicar máscara de CNPJ
    const maskCNPJ = (value) =>
        value
            .replace(/\D/g, '') // Remove tudo que não é dígito
            .replace(/(\d{2})(\d)/, '$1.$2') // Coloca ponto após os 2 primeiros dígitos
            .replace(/(\d{3})(\d)/, '$1.$2') // Coloca ponto após os 3 segundos dígitos
            .replace(/(\d{3})(\d)/, '$1/$2') // Coloca barra após os 3 terceiros dígitos
            .replace(/(\d{4})(\d)/, '$1-$2') // Coloca hífen após os 4 quartos dígitos
            .replace(/(-\d{2})\d+?$/, '$1'); // Limita a 14 dígitos

    // Função para aplicar a máscara correta
    const applyDocumentMask = (value) => {
        const cleanValue = value.replace(/\D/g, '');
        if (typeDocument === 'CPF') {
            return maskCPF(value);
        }
        return maskCNPJ(value);
    };

    // Função para aplicar máscara de CEP
    const maskCEP = (value) =>
        value
            .replace(/\D/g, '')
            .replace(/(\d{5})(\d)/, '$1-$2')
            .replace(/(-\d{3})\d+?$/, '$1');

    // Função para aplicar máscara de DDD
    const maskDDD = (value) => value.replace(/\D/g, '').slice(0, 2);

    // Função para aplicar máscara de telefone
    const maskPhone = (value) => {
        const cleanValue = value.replace(/\D/g, '');
        if (typePhone === 'CELULAR') {
            return cleanValue
                .replace(/(\d{5})(\d)/, '$1-$2')
                .replace(/(-\d{4})\d+?$/, '$1');
        }
        return cleanValue
            .replace(/(\d{4})(\d)/, '$1-$2')
            .replace(/(-\d{4})\d+?$/, '$1');
    };

    const defaultValues = {
        cpf_cnpj: establishment?.cpf_cnpj || '',
        nomeDoEstabelecimento: establishment?.nomeDoEstabelecimento || '',
        nome: establishment?.nome || '',
        qta: establishment?.qta || '',
        ddd: establishment?.telefone?.ddd || '',
        phone: establishment?.telefone?.numeroTelefone || '',
        cep: establishment?.endereco?.cep || '',
        address: establishment?.endereco?.rua || '',
        numeroEndereco: establishment?.endereco?.numeroEndereco || '',
        complement: establishment?.endereco?.complemento || '',
        neighborhood: establishment?.endereco?.bairro || '',
        city: establishment?.endereco?.cidade || '',
        state: establishment?.endereco?.uf || '',
    };

    const methods = useForm({
        resolver: zodResolver(CompleteProfileSchema),
        defaultValues,
        mode: 'onChange',
    });

    const {
        handleSubmit,
        formState: { isSubmitting, errors },
        setValue,
        watch,
    } = methods;

    // Handler para mudança no campo de documento
    const handleDocumentChange = (e) => {
        const { value } = e.target;
        const maskedValue = applyDocumentMask(value);
        setValue('cpf_cnpj', maskedValue);
    };

    // Handlers para campos com máscara
    const handleCEPChange = (e) => {
        const maskedValue = maskCEP(e.target.value);
        setValue('cep', maskedValue);
    };

    const handleDDDChange = (e) => {
        const maskedValue = maskDDD(e.target.value);
        setValue('ddd', maskedValue);
    };

    const handlePhoneChange = (e) => {
        const maskedValue = maskPhone(e.target.value);
        setValue('phone', maskedValue);
    };

    // Limpa o campo quando muda o tipo de documento
    useEffect(() => {
        setValue('cpf_cnpj', '');
    }, [typeDocument, setValue]);

    const handleGetAddressByCep = async () => {
        const cep = watch('cep')?.replace(/\D/g, ''); // Remove a máscara
        if (cep && cep.length >= 8) {
            try {
                const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
                const data = await response.json();

                if (!data.erro) {
                    setValue('address', data.logradouro);
                    setValue('neighborhood', data.bairro);
                    setValue('city', data.localidade);
                    setValue('state', data.uf);
                    setIbge(data.ibge);
                    toast.success('Endereço encontrado!');
                } else {
                    toast.error('CEP não encontrado!');
                }
            } catch (error) {
                console.error('Error:', error);
                toast.error('Erro ao buscar CEP!');
            }
        }
    };

    const onSubmit = handleSubmit(async (data) => {
        try {
            const accessToken = localStorage.getItem(STORAGE_KEY);

            const tipeDocumentValues = {
                "CPF": { codigo: 0 },
                "CNPJ": { codigo: 1 },
            };

            // Remove máscaras dos campos
            const cleanCpfCnpj = data.cpf_cnpj.replace(/\D/g, '');
            const cleanCep = data.cep.replace(/\D/g, '');
            const cleanPhone = data.phone.replace(/\D/g, '');

            const dataToSend = {
                ...establishment,
                tipoDocumento: tipeDocumentValues[typeDocument].codigo,
                cpf_cnpj: cleanCpfCnpj,
                nomeDoEstabelecimento: data.nomeDoEstabelecimento || '',
                nome: data.nome,
                qta: data.qta,
                telefone: {
                    ddd: data.ddd,
                    numeroTelefone: cleanPhone,
                    tipoTelefone: typePhone
                },
                endereco: {
                    cep: cleanCep,
                    rua: data.address,
                    numero: data.numeroEndereco,
                    complemento: data.complement || '',
                    bairro: data.neighborhood,
                    cidade: data.city,
                    city: ibge || 4108304,
                    numeroEndereco: data.numeroEndereco,
                    uf: Object.keys(estadosEst).find(key => estadosEst[key].nome === data.state) || data.state
                }
            };

            const response = await fetch(`${API_CONFIG.BASE_URL}/api/estabelecimento/${establishment.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${accessToken}`
                },
                body: JSON.stringify(dataToSend)
            });

            if (!response.ok) {
                throw new Error('Erro ao atualizar estabelecimento!');
            }

            toast.success('Perfil completado com sucesso!');
            onSuccess?.();
            onClose();
            window.location.reload();
        } catch (error) {
            console.error(error);
            toast.error('Erro ao completar perfil!');
        }
    });

    const handleNext = async () => {
        const fieldsToValidate = {
            0: ['cpf_cnpj'], // Documento
            1: ['nome', 'qta'], // Informações
            2: ['ddd', 'phone'], // Contato
            3: ['cep', 'address', 'numeroEndereco', 'neighborhood', 'city', 'state'], // Endereço
        };

        const fields = fieldsToValidate[activeStep];
        const valid = await methods.trigger(fields);

        if (valid) {
            if (activeStep === steps.length - 1) {
                handleSubmit(onSubmit)();
            } else {
                setActiveStep((prevStep) => prevStep + 1);
            }
        }
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Stack spacing={3}>
                        <Typography variant="body2" color="text.secondary">
                            Para começar, precisamos do seu documento de identificação fiscal.
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel id="select-type-document">Tipo de Documento</InputLabel>
                            <Select
                                labelId="select-type-document"
                                value={typeDocument}
                                label="Tipo de Documento"
                                onChange={(e) => setTypeDocument(e.target.value)}
                            >
                                <MenuItem value="CPF">CPF</MenuItem>
                                <MenuItem value="CNPJ">CNPJ</MenuItem>
                            </Select>
                        </FormControl>

                        <Field.Text
                            name="cpf_cnpj"
                            label={typeDocument === 'CPF' ? 'CPF' : 'CNPJ'}
                            placeholder={typeDocument === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00'}
                            value={watch('cpf_cnpj')}
                            onChange={handleDocumentChange}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ maxLength: typeDocument === 'CPF' ? 14 : 18 }}
                        />
                    </Stack>
                );

            case 1:
                return (
                    <Stack spacing={3}>
                        <Typography variant="body2" color="text.secondary">
                            Precisamos de algumas informações adicionais sobre o estabelecimento.
                        </Typography>

                        <Field.Text
                            value={watch('nomeDoEstabelecimento')}
                            name="nomeDoEstabelecimento"
                            label="Nome Fantasia (Opcional)"
                            placeholder="Como seu estabelecimento é conhecido"
                            InputLabelProps={{ shrink: true }}
                        />

                        <Field.Text
                            value={watch('nome')}
                            name="nome"
                            label="Nome do Responsável*"
                            placeholder="Nome completo do responsável"
                            InputLabelProps={{ shrink: true }}
                        />

                        <FormControl fullWidth>
                            <InputLabel id="select-qta-profissionais">Quantidade de Profissionais*</InputLabel>
                            <Select
                                labelId="select-qta-profissionais"
                                value={watch('qta')}
                                label="Quantidade de Profissionais*"
                                onChange={(e) => setValue('qta', e.target.value)}
                            >
                                <MenuItem value="1">1</MenuItem>
                                <MenuItem value="3">1 a 3</MenuItem>
                                <MenuItem value="6">4 a 6</MenuItem>
                                <MenuItem value="7">Ilimitado</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                );

            case 2:
                return (
                    <Stack spacing={3}>
                        <Typography variant="body2" color="text.secondary">
                            Agora precisamos de um contato para seus clientes.
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel id="select-type-phone">Tipo de Telefone</InputLabel>
                            <Select
                                labelId="select-type-phone"
                                value={typePhone}
                                label="Tipo de Telefone"
                                onChange={(e) => setTypePhone(e.target.value)}
                            >
                                <MenuItem value="CELULAR">Celular</MenuItem>
                                <MenuItem value="FIXO">Fixo</MenuItem>
                            </Select>
                        </FormControl>

                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Field.Text
                                    name="ddd"
                                    label="DDD"
                                    placeholder="11"
                                    value={watch('ddd')}
                                    onChange={handleDDDChange}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ maxLength: 2 }}
                                />
                            </Grid>
                            <Grid item xs={8}>
                                <Field.Text
                                    name="phone"
                                    label="Telefone"
                                    placeholder={typePhone === 'CELULAR' ? '98765-4321' : '3456-7890'}
                                    value={watch('phone')}
                                    onChange={handlePhoneChange}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ maxLength: 10 }}
                                />
                            </Grid>
                        </Grid>
                    </Stack>
                );

            case 3:
                return (
                    <Stack spacing={3}>
                        <Typography variant="body2" color="text.secondary">
                            Por último, precisamos do endereço do seu estabelecimento.
                        </Typography>

                        <Field.Text
                            name="cep"
                            label="CEP"
                            placeholder="00000-000"
                            value={watch('cep')}
                            onChange={handleCEPChange}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ maxLength: 9 }}
                            onBlur={handleGetAddressByCep}
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={8}>
                                <Field.Text
                                    name="address"
                                    label="Endereço"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <Field.Text
                                    name="numeroEndereco"
                                    label="Número"
                                    InputLabelProps={{ shrink: true }}
                                    value={watch('numeroEndereco')}
                                />
                            </Grid>
                        </Grid>

                        <Field.Text
                            name="complement"
                            label="Complemento (Opcional)"
                            InputLabelProps={{ shrink: true }}
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Field.Text
                                    name="neighborhood"
                                    label="Bairro"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Field.Text
                                    name="city"
                                    label="Cidade"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>

                        <Field.Text
                            name="state"
                            label="Estado"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Stack>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle>
                <Stack spacing={1}>
                    <Typography variant="h5">Complete seu perfil</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Para aproveitar todos os recursos da plataforma, precisamos de mais algumas informações.
                    </Typography>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mt: 2, mb: 3 }}>
                    <Stepper activeStep={activeStep}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                <Form methods={methods} onSubmit={onSubmit}>
                    <Box sx={{ mt: 3 }}>
                        {renderStepContent(activeStep)}
                    </Box>
                </Form>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                >
                    Voltar
                </Button>
                <Button
                    onClick={onClose}
                    variant="outlined"
                >
                    Pular por agora
                </Button>
                <LoadingButton
                    variant="contained"
                    onClick={handleNext}
                    loading={isSubmitting}
                >
                    {activeStep === steps.length - 1 ? 'Concluir' : 'Próximo'}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
